import React, { useReducer, useCallback, useMemo, useRef, useState, useEffect } from "react";
import { useQuery, useLazyQuery, useApolloClient } from "@apollo/client";
import { Modal, Spinner, Text } from "@nike/eds";
import { GET_CONSIGNMENT_SHIPMENTS, GET_SHIPMENT_PRODUCTS } from "../../graphql/shipments.graphql";
import { ShipmentsTable } from "../shipments-table/ShipmentsTable";
import type { RowSelectionState, PaginationState, ExpandedState } from "@tanstack/react-table";
import type { TableRef } from "../base-table";
import type { Shipment, ProductComponent, ShipmentProduct, StpDetails, User } from "../../graphql/graphql";

import { ExpandedShipmentProducts } from "./ExpandedShipmentProducts";
import { InvoicesOrdersModalContent } from "./InvoicesOrdersModalContent";
import { StpDetailsModalContent } from "./StpDetailsModalContent";
import { ClassificationDetailsModalContent } from "./ClassificationDetailsModalContent";
import { getShipmentProductsTableColumns } from "../shipment-products-table/ShipmentProductsTableColumns";
import { UserUISettingsService } from "../../services/UserUISettingsService";
import { UISettings, TableUISettings, DEFAULT_UI_SETTINGS } from "../../shared/Types";

type ShipmentsProductsState = {
  loading: boolean;
  error: string | null;
  shipments: Shipment[];
  pagination: PaginationState & { totalCount?: number };
  shipmentProductsPagination: PaginationState & { totalCount?: number };
  rowSelection: RowSelectionState;
  expanded?: ExpandedState;
  showClassificationModal: boolean;
  classificationComponents: ProductComponent[] | [];
  showInvoicesOrdersModal: boolean;
  invoiceOrdersProduct: ShipmentProduct | null;
  expandedProductsLoading: boolean;
  expandedProductsError: string | null;
  expandedProducts: ShipmentProduct[];
  stpDetails: StpDetails[] | [];
  showStpDetailsModal: boolean;
};

type ShipmentsProductsAction =
  | { type: "FETCH_START" }
  | { type: "FETCH_SUCCESS"; payload: { shipments: Shipment[]; totalCount: number } }
  | { type: "FETCH_ERROR"; payload: string }
  | { type: "SET_PAGINATION"; payload: PaginationState }
  | { type: "SET_SHIPMENT_PRODUCTS_PAGINATION"; payload: PaginationState }
  | { type: "SET_ROW_SELECTION"; payload: RowSelectionState }
  | { type: "SET_EXPANDED"; payload: ExpandedState }
  | { type: "SHOW_CLASSIFICATION_MODAL"; payload: ProductComponent[] }
  | { type: "HIDE_CLASSIFICATION_MODAL" }
  | { type: "CLEAR_CLASSIFICATION_COMPONENTS" }
  | { type: "SHOW_INVOICES_ORDERS_MODAL"; payload: ShipmentProduct | null }
  | { type: "HIDE_INVOICES_ORDERS_MODAL" }
  | { type: "CLEAR_INVOICES_ORDERS_PRODUCT" }
  | { type: "EXPANDED_PRODUCTS_FETCH_START" }
  | { type: "EXPANDED_PRODUCTS_FETCH_SUCCESS"; payload: { products: ShipmentProduct[]; totalCount?: number } }
  | { type: "EXPANDED_PRODUCTS_FETCH_ERROR"; payload: string }
  | { type: "EXPANDED_PRODUCTS_CLEAR" }
  | { type: "SHOW_STP_DETAILS_MODAL"; payload: StpDetails[] }
  | { type: "HIDE_STP_DETAILS_MODAL" }
  | { type: "CLEAR_STP_DETAILS" };

function shipmentsProductsReducer(state: ShipmentsProductsState, action: ShipmentsProductsAction): ShipmentsProductsState {
  // Log every action and the resulting state for debugging
  // console.log("[ShipmentsProductsReducer] Action:", action);
  const nextState = (() => {
    switch (action.type) {
      case "FETCH_START":
        return { ...state, loading: true, error: null };
      case "FETCH_SUCCESS":
        return {
          ...state,
          loading: false,
          shipments: action.payload.shipments,
          pagination: { ...state.pagination, totalCount: action.payload.totalCount }
        };
      case "FETCH_ERROR":
        return { ...state, loading: false, error: action.payload };
      case "SET_PAGINATION":
        return { ...state, pagination: { ...state.pagination, ...action.payload } };
      case "SET_SHIPMENT_PRODUCTS_PAGINATION":
        return { ...state, shipmentProductsPagination: { ...state.shipmentProductsPagination, ...action.payload } };
      case "SET_ROW_SELECTION":
        return { ...state, rowSelection: action.payload };
      case "SET_EXPANDED":
        return { ...state, expanded: action.payload };
      case "SHOW_CLASSIFICATION_MODAL":
        return { ...state, showClassificationModal: true, classificationComponents: action.payload };
      case "HIDE_CLASSIFICATION_MODAL":
        return { ...state, showClassificationModal: false };
      case "CLEAR_CLASSIFICATION_COMPONENTS":
        return { ...state, classificationComponents: [] };
      case "SHOW_INVOICES_ORDERS_MODAL":
        return { ...state, showInvoicesOrdersModal: true, invoiceOrdersProduct: action.payload };
      case "HIDE_INVOICES_ORDERS_MODAL":
        return { ...state, showInvoicesOrdersModal: false };
      case "CLEAR_INVOICES_ORDERS_PRODUCT":
        return { ...state, invoiceOrdersProduct: null };
      case "EXPANDED_PRODUCTS_FETCH_START":
        return { ...state, expandedProductsLoading: true, expandedProductsError: null, expandedProducts: [] };
      case "EXPANDED_PRODUCTS_FETCH_SUCCESS":
        return { ...state, expandedProductsLoading: false, expandedProducts: action.payload.products, shipmentProductsPagination: { ...state.shipmentProductsPagination, totalCount: action.payload.totalCount } };
      case "EXPANDED_PRODUCTS_FETCH_ERROR":
        return { ...state, expandedProductsLoading: false, expandedProductsError: action.payload };
      case "EXPANDED_PRODUCTS_CLEAR":
        return { ...state, expandedProductsLoading: false, expandedProductsError: null, expandedProducts: [] };
      case "SHOW_STP_DETAILS_MODAL":
        return { ...state, showStpDetailsModal: true, stpDetails: action.payload || [] };
      case "HIDE_STP_DETAILS_MODAL":
        return { ...state, showStpDetailsModal: false };
      case "CLEAR_STP_DETAILS":
        return { ...state, stpDetails: [] };
      default:
        return state;
    }
  })();
  // console.log("[ShipmentsProductsReducer] Next State:", nextState);
  return nextState;
}

export const ShipmentsProducts = ({ consignmentId, currentUser }: { consignmentId: string, currentUser?: User | undefined }) => {

  const client = useApolloClient();
  // Detect E2E test runtime
  const isE2E = typeof window !== 'undefined' && (window as any).__E2E__ === true;
  const settingsService = useMemo(() => new UserUISettingsService(client, isE2E), [client, isE2E]);
  const [settings, setSettings] = useState<UISettings | undefined>(undefined);

  const settingsKey = "shipmentProducts";

  const parsedUISettings: UISettings | undefined = (() => {
    const raw = currentUser?.settings?.uiSettings;
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw) as UISettings;
      } catch (e) {
        console.error("Failed to parse uiSettings:", raw, e);
        return undefined;
      }
    }
    return raw as UISettings | undefined;
  })();

  // double check handlers passed in here are ok
  const tableSettings: TableUISettings | undefined = {
    pageSize: parsedUISettings?.[`${settingsKey}PageSize`] as number || 10,
    columnOrder: parsedUISettings?.[`${settingsKey}ColumnOrder`] as string[] || getShipmentProductsTableColumns(() => {}, () => {}, () => {}).map(col => col.accessorKey as string),
    columnVisibility: parsedUISettings?.[`${settingsKey}ColumnVisibility`] as Record<string, boolean> || {},
  };

  useEffect(() => {
      settingsService.getSettings().then(setSettings);
  }, [settingsService]);
  
  const [state, dispatch] = useReducer(shipmentsProductsReducer, {
    loading: false,
    error: null,
    shipments: [],
    pagination: { pageIndex: 0, pageSize: 10, totalCount: undefined },
    shipmentProductsPagination: { pageIndex: 0, pageSize: tableSettings?.pageSize || 10, totalCount: undefined },
    rowSelection: {},
    expanded: {},
    showClassificationModal: false,
    classificationComponents: [],
    showInvoicesOrdersModal: false,
    invoiceOrdersProduct: null,
    expandedProductsLoading: false,
    expandedProductsError: null,
    expandedProducts: [],
    stpDetails: [],
    showStpDetailsModal: false,
  });

    useEffect(() => {
      if (
        state.shipments.length === 1 &&
        Object.keys(state.rowSelection).length === 0 &&
        Object.keys(state.expanded ?? {}).length === 0
      ) {
        const shipment = state.shipments[0];
        dispatch({ type: "SET_ROW_SELECTION", payload: { [shipment.shipmentNbr]: true } });
        dispatch({ type: "SET_EXPANDED", payload: { [shipment.shipmentNbr]: true } });
        setExpandedShipment(shipment);
      }
    }, [state.shipments, state.rowSelection, state.expanded]);

  // Track which shipment is expanded
  const [expandedShipment, setExpandedShipment] = useState<Shipment | null>(null);

  const shipmentProductsTableRef = useRef<TableRef>(null);
  const classificationTableRef = useRef<TableRef>(null);
  const invoicesOrdersTableRef = useRef<TableRef>(null);
  const stpDetailsTableRef = useRef<TableRef>(null);

  const { loading, error, data } = useQuery(GET_CONSIGNMENT_SHIPMENTS, {
    variables: {
      consignmentId: consignmentId,
      limit: state.pagination.pageSize,
      offset: state.pagination.pageIndex * state.pagination.pageSize,
    },
    skip: !consignmentId,
    fetchPolicy: isE2E ? "cache-first" : "cache-and-network",
  });

  useEffect(() => {
    if (!data) return;
    const shipmentsData = data?.shipment?.list;
    if (shipmentsData.pageInfo?.status === "ERROR") {
      dispatch({ type: "FETCH_ERROR", payload: shipmentsData.pageInfo?.message });
    } else {
      dispatch({
        type: "FETCH_SUCCESS",
        payload: {
          shipments: shipmentsData?.items || [],
          totalCount: shipmentsData?.pageInfo?.totalCount || 0,
        }
      });
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      dispatch({ type: "FETCH_ERROR", payload: error.message });
    }
  }, [error]);

  const isLoading = loading || state.loading;

  const handleHtsCodeView = useCallback((args: { components: ProductComponent[] }) => {
    dispatch({ type: "SHOW_CLASSIFICATION_MODAL", payload: args.components || [] });
  }, []);

  const handleShowInvoicesOrdersView = useCallback((args: { product: ShipmentProduct | null }) => {
    dispatch({ type: "SHOW_INVOICES_ORDERS_MODAL", payload: args.product });
  }, []);

  const handleStpDetailsView = useCallback((args: { stpDetails: StpDetails[] | [] }) => {
    dispatch({ type: "SHOW_STP_DETAILS_MODAL", payload: args.stpDetails });
  }, []);

  // --- Shipments table columns ---
  const columns = useMemo(() => [
    {
      header: () => <span title="Shipment Number">Shipment Number</span>,
      accessorKey: "shipmentNbr",
      cell: ({ row }: any) => row.original.shipmentNbr,
      size: 260,
      enableSorting: false,
      enableColumnFilter: false,
    },
    {
      header: () => <span title="Count of Delivery Items"># of Delivery Items</span>,
      accessorKey: "shipmentProductCount",
      cell: ({ row }: any) => row.original.shipmentProductCount,
      size: 260,
      enableSorting: false,
      enableColumnFilter: false,
    },
  ], []);

  // --- Expanded shipment products logic ---
  const [fetchShipmentProducts] = useLazyQuery(GET_SHIPMENT_PRODUCTS, { fetchPolicy: "cache-first" });

  // Fetch products when expandedShipment changes
  useEffect(() => {
    if (expandedShipment) {
      dispatch({ type: "EXPANDED_PRODUCTS_FETCH_START" });
      fetchShipmentProducts({
        variables: {
          consignmentId,
          shipmentNbr: expandedShipment.shipmentNbr,
          offset: state.shipmentProductsPagination.pageIndex * state.shipmentProductsPagination.pageSize,
          limit: state.shipmentProductsPagination.pageSize,
        },
      })
        .then(({ data }) => {
          const productsData = data?.shipment?.listProducts;
          const products = productsData?.items || [];
          dispatch({ type: "EXPANDED_PRODUCTS_FETCH_SUCCESS", payload: { products, totalCount: productsData?.pageInfo?.totalCount } });
        })
        .catch((err: any) => {
          dispatch({ type: "EXPANDED_PRODUCTS_FETCH_ERROR", payload: err.message });
        });
    }
  }, [expandedShipment, consignmentId, state.shipmentProductsPagination.pageIndex, state.shipmentProductsPagination.pageSize, fetchShipmentProducts]);

  // Render expanded content (no side effects here!)
  const renderExpandedContent = useCallback(
    (shipment: Shipment) => (
      <ExpandedShipmentProducts
        loading={state.expandedProductsLoading}
        error={state.expandedProductsError}
        products={state.expandedProducts}
        shipmentProductsTableRef={shipmentProductsTableRef}
        handleHtsCodeView={handleHtsCodeView}
        handleShowInvoicesOrdersView={handleShowInvoicesOrdersView}
        handleStpDetailsView={handleStpDetailsView}
        pageIndex={state.shipmentProductsPagination.pageIndex}
        pageSize={state.shipmentProductsPagination.pageSize}
        totalCount={state.shipmentProductsPagination.totalCount || 0}
        onPaginationChange={(pagination) => {
          dispatch({ type: "SET_SHIPMENT_PRODUCTS_PAGINATION", payload: pagination });
              const newSettings = settingsService.setTablePageSize(
              settings || DEFAULT_UI_SETTINGS,
              settingsKey,
              pagination.pageSize
            );
            settingsService.changeSettings(newSettings).then(setSettings);
        }}
      />
    ),
    [
      state.expandedProductsLoading,
      state.expandedProductsError,
      state.expandedProducts,
      handleHtsCodeView,
      handleShowInvoicesOrdersView,
      handleStpDetailsView,
      state.shipmentProductsPagination.pageIndex,
      state.shipmentProductsPagination.pageSize,
      state.shipmentProductsPagination.totalCount,
      settingsService,
      settings,
      settingsKey
    ]
  );

  // Track mount status
  const isMountedRef = useRef(true);

  // Store timeout IDs for cleanup
  const classificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const invoicesOrdersTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const stpDetailsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (classificationTimeoutRef.current) clearTimeout(classificationTimeoutRef.current);
      if (invoicesOrdersTimeoutRef.current) clearTimeout(invoicesOrdersTimeoutRef.current);
      if (stpDetailsTimeoutRef.current) clearTimeout(stpDetailsTimeoutRef.current);
    };
  }, []);

  return (
    <>
      {isLoading && (
        <div className="eds-flex eds-flex--justify-content-center" style={{ height: "200px" }}>
          <Spinner size="large" />
        </div>
      )}
      {state.error && <Text font="body-3" as="div" color="danger">{state.error}</Text>}

      {!isLoading && !state.error && (
        <ShipmentsTable
          columns={() => columns}
          data={state.shipments}
          enablePagination={state.shipments?.length > 1}
          enableRowSelection={true}
          pageIndex={state.pagination.pageIndex}
          pageSize={state.pagination.pageSize}
          totalCount={state.pagination.totalCount}
          onPaginationChange={(pagination) => {
            dispatch({ type: "SET_PAGINATION", payload: pagination });
          }}
          state={{
            sorting: [],
            columnFilters: [],
            setSorting: () => {},
            setColumnFilters: () => {},
            columnOrder: [],
            setColumnOrder: () => {},
            setRowSelection: (newRowSelection: RowSelectionState) => {
              const rowId = Object.keys(newRowSelection)[0];
              const isSelected = !!state.rowSelection[rowId];
              const isExpanded = !!state.expanded?.[rowId];

              if (isSelected && isExpanded) {
                // Deselect and collapse
                dispatch({ type: "SET_ROW_SELECTION", payload: {} });
                dispatch({ type: "SET_EXPANDED", payload: {} });
                dispatch({ type: "EXPANDED_PRODUCTS_CLEAR" });
                setExpandedShipment(null);
              } else {
                // Select and expand
                dispatch({ type: "SET_ROW_SELECTION", payload: { [rowId]: true } });
                dispatch({ type: "SET_EXPANDED", payload: { [rowId]: true } });
                const shipment = state.shipments.find(s => s.shipmentNbr === rowId) || null;
                setExpandedShipment(shipment);
              }
            },
            rowSelection: state.rowSelection,
            resetRowSelection: () => {
              dispatch({ type: "SET_ROW_SELECTION", payload: {} });
              setExpandedShipment(null);
            },
            columnVisibility: {},
            pagination: { totalCount: state.pagination.totalCount },
            globalSearch: "",
            expanded: state.expanded,
            columnFiltersSidePanelIsOpen: false,
          }}
          onExpandedChange={(expanded: ExpandedState) => {
            dispatch({ type: "SET_EXPANDED", payload: expanded });
            if (Object.keys(expanded).length === 0) {
              dispatch({ type: "EXPANDED_PRODUCTS_CLEAR" });
              setExpandedShipment(null);
            }
          }}
          getRowId={(row) => row.shipmentNbr}
          renderExpandedContent={renderExpandedContent}
          isLoading={state.loading}
          error={state.error}
          meta={{}}
        />
      )}

      <Modal
        isOpen={state.showClassificationModal}
        onDismiss={() => {
          dispatch({ type: "HIDE_CLASSIFICATION_MODAL" });
          if (classificationTimeoutRef.current) clearTimeout(classificationTimeoutRef.current);
          classificationTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              dispatch({ type: "CLEAR_CLASSIFICATION_COMPONENTS" });
            }
          }, 300);
        }}
        headerSlot="Classification Details"
      >
        <ClassificationDetailsModalContent
          tableRef={classificationTableRef}
          components={
            [...state.classificationComponents].sort(
              (a, b) => (Number(b.percentage) ?? 0) - (Number(a.percentage) ?? 0)
            )
          }
        />
      </Modal>

      <Modal
        isOpen={state.showInvoicesOrdersModal}
        onDismiss={() => {
          dispatch({ type: "HIDE_INVOICES_ORDERS_MODAL" });
          if (invoicesOrdersTimeoutRef.current) clearTimeout(invoicesOrdersTimeoutRef.current);
          invoicesOrdersTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              dispatch({ type: "CLEAR_INVOICES_ORDERS_PRODUCT" });
            }
          }, 300);
        }}
        headerSlot="Invoices & Orders"
      >
        <InvoicesOrdersModalContent tableRef={invoicesOrdersTableRef} shipmentProduct={state.invoiceOrdersProduct} />
      </Modal>

      <Modal
        isOpen={state.showStpDetailsModal}
        onDismiss={() => {
          dispatch({ type: "HIDE_STP_DETAILS_MODAL" });
          if (stpDetailsTimeoutRef.current) clearTimeout(stpDetailsTimeoutRef.current);
          stpDetailsTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              dispatch({ type: "CLEAR_STP_DETAILS" });
            }
          }, 300);
        }}
        headerSlot="STP Details"
      >
        <StpDetailsModalContent
          tableRef={stpDetailsTableRef}
          stpDetailsArray={state.stpDetails || []}
        />
      </Modal>
    </>
  );
};
